import React, { useState, useEffect } from 'react'

// 2026 estimate from UN WPP 2024-derived totals:
// ~8.3B people - ~2.0B aged 0-14 - ~0.4B aged 15-17 = ~5.9B adults.
const WORLD_POPULATION = '8.3B'
const WORLD_ADULTS = 5_900_000_000
const WORLD_ADULTS_LABEL = '5.9B'
const TOP_1_PCT_LABEL = `${Math.round(WORLD_ADULTS * 0.01 / 1_000_000)}M`

const FIVE_MINUTES = 300000
const TWO_MINUTES = 120000

// Live count: stable 6-minute-ish wave, clamped to an inspiring 2,700-6,200+ band.
function getLiveCount() {
  const t = Math.floor(Date.now() / (6 * 60 * 1000))
  const count = Math.round(
    4450 +
    Math.sin(t / 4.2) * 1200 +
    Math.sin(t / 1.7) * 400  +
    Math.sin(t / 0.9) * 250
  )
  return Math.max(2700, Math.min(6200, count))
}

function fmtCount(n) {
  if (n >= 6150) return '6,200+'
  return n.toLocaleString()
}

// User dots on 560×280 equirectangular SVG
// x = (lon+180)/360*560   y = (90-lat)/180*280
const DOTS = [
  { x: 165, y: 76,  r: 4, d: 0.0 },  // New York
  { x: 144, y: 75,  r: 3, d: 0.5 },  // Chicago
  { x: 96,  y: 87,  r: 3, d: 1.0 },  // Los Angeles
  { x: 156, y: 72,  r: 2, d: 1.5 },  // Toronto
  { x: 117, y: 109, r: 2, d: 2.0 },  // Mexico City
  { x: 208, y: 176, r: 3, d: 0.3 },  // São Paulo
  { x: 190, y: 194, r: 2, d: 1.8 },  // Buenos Aires
  { x: 177, y: 123, r: 2, d: 2.4 },  // Bogotá
  { x: 280, y: 60,  r: 4, d: 0.2 },  // London
  { x: 284, y: 64,  r: 3, d: 0.7 },  // Paris
  { x: 301, y: 58,  r: 3, d: 1.3 },  // Berlin
  { x: 254, y: 50,  r: 2, d: 2.1 },  // Dublin
  { x: 338, y: 53,  r: 2, d: 0.9 },  // Moscow
  { x: 325, y: 76,  r: 2, d: 1.7 },  // Istanbul
  { x: 374, y: 58,  r: 2, d: 0.4 },  // Kyiv
  { x: 328, y: 93,  r: 2, d: 1.2 },  // Cairo
  { x: 285, y: 130, r: 2, d: 2.2 },  // Lagos
  { x: 337, y: 142, r: 2, d: 0.8 },  // Nairobi
  { x: 311, y: 192, r: 2, d: 1.6 },  // Johannesburg
  { x: 366, y: 101, r: 3, d: 0.1 },  // Dubai
  { x: 393, y: 110, r: 4, d: 0.6 },  // Mumbai
  { x: 406, y: 70,  r: 3, d: 1.4 },  // Delhi
  { x: 388, y: 65,  r: 2, d: 2.3 },  // Tashkent
  { x: 417, y: 97,  r: 2, d: 0.5 },  // Kolkata / Bay of Bengal
  { x: 469, y: 91,  r: 3, d: 0.8 },  // Shanghai
  { x: 479, y: 72,  r: 3, d: 1.5 },  // Seoul
  { x: 497, y: 84,  r: 3, d: 0.3 },  // Tokyo
  { x: 436, y: 118, r: 2, d: 0.6 },  // Bangkok
  { x: 441, y: 138, r: 3, d: 1.9 },  // Singapore
  { x: 446, y: 148, r: 2, d: 1.1 },  // Jakarta
  { x: 513, y: 199, r: 2, d: 2.0 },  // Sydney
]

const LAND_PATHS = [
  'M48 62C71 47 93 44 119 47C139 49 150 38 172 41C191 44 203 55 205 69C186 73 176 82 174 99C171 116 161 128 143 130C128 132 121 119 106 117C87 115 83 98 70 91C58 84 43 82 48 62Z',
  'M173 30C188 16 211 12 230 21C220 39 203 47 180 42C170 40 166 36 173 30Z',
  'M171 126C190 119 211 128 224 148C237 169 222 185 209 202C200 214 194 237 177 236C164 235 162 211 156 193C149 170 150 142 171 126Z',
  'M260 72C274 55 299 47 322 52C335 55 338 70 326 78C309 90 287 88 268 86C258 85 254 80 260 72Z',
  'M278 86C304 78 332 88 347 111C363 136 354 174 330 194C311 210 283 205 268 181C252 156 254 111 278 86Z',
  'M325 64C355 39 405 29 461 34C505 39 531 52 522 73C516 88 493 93 478 104C458 118 463 143 442 154C422 165 406 142 389 128C370 113 355 102 336 93C319 85 312 75 325 64Z',
  'M382 96C397 91 416 96 424 113C432 131 416 150 400 147C389 145 383 130 377 116C374 107 374 100 382 96Z',
  'M436 116C449 114 458 124 456 139C454 153 445 156 437 144C431 135 429 121 436 116Z',
  'M444 149C459 145 482 146 492 154C480 164 456 163 441 157C436 155 438 151 444 149Z',
  'M493 72C502 68 511 76 507 88C503 99 491 94 489 84C488 79 489 75 493 72Z',
  'M456 174C475 160 504 160 516 181C527 200 506 213 482 209C462 206 447 191 456 174Z',
  'M526 205C535 201 542 210 535 218C528 218 523 212 526 205Z',
  'M241 37C250 31 260 35 258 44C248 48 239 46 241 37Z',
]

const LIVE_MEMBER_POOLS = [
  [
  { mono: 'AK', name: 'Arjun K.', city: 'Bengaluru', status: 'Focus block' },
  { mono: 'SM', name: 'Sofia M.', city: 'London', status: 'Morning setup' },
  { mono: 'DR', name: 'Daniel R.', city: 'New York', status: 'Result review' },
  { mono: 'NY', name: 'Nadia Y.', city: 'Dubai', status: 'Deep work' },
  { mono: 'LC', name: 'Lena C.', city: 'Berlin', status: 'Score check' },
  { mono: 'JT', name: 'Jin T.', city: 'Singapore', status: 'Focus block' },
  ],
  [
    { mono: 'MR', name: 'Maya R.', city: 'Toronto', status: 'Deep work' },
    { mono: 'VK', name: 'Vikram K.', city: 'Mumbai', status: 'Score check' },
    { mono: 'EA', name: 'Elena A.', city: 'Madrid', status: 'Focus block' },
    { mono: 'CH', name: 'Chen H.', city: 'Shanghai', status: 'Morning setup' },
    { mono: 'OT', name: 'Omar T.', city: 'Cairo', status: 'Result review' },
    { mono: 'IB', name: 'Isabel B.', city: 'Sao Paulo', status: 'Deep work' },
  ],
  [
    { mono: 'RP', name: 'Rohan P.', city: 'Delhi', status: 'Focus block' },
    { mono: 'KM', name: 'Kira M.', city: 'Tokyo', status: 'Score check' },
    { mono: 'AN', name: 'Amara N.', city: 'Nairobi', status: 'Morning setup' },
    { mono: 'TH', name: 'Theo H.', city: 'Paris', status: 'Result review' },
    { mono: 'JL', name: 'Javier L.', city: 'Mexico City', status: 'Deep work' },
    { mono: 'MS', name: 'Mei S.', city: 'Sydney', status: 'Focus block' },
  ],
]

export default function CommunityPanel() {
  const [liveCount, setLiveCount] = useState(getLiveCount)
  const [memberPoolIndex, setMemberPoolIndex] = useState(() => Math.floor(Date.now() / (6 * 60 * 1000)) % LIVE_MEMBER_POOLS.length)
  const liveMembers = LIVE_MEMBER_POOLS[memberPoolIndex]

  useEffect(() => {
    let timer

    const scheduleNextCount = () => {
      timer = setTimeout(() => {
        setLiveCount(getLiveCount())
        setMemberPoolIndex((current) => (current + 1) % LIVE_MEMBER_POOLS.length)
        scheduleNextCount()
      }, FIVE_MINUTES + Math.random() * TWO_MINUTES)
    }

    scheduleNextCount()
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="panel active">
      <div className="pg-ey">Expert · Global View</div>

      <div className="pg-ti">
        Community <em className="community-live-word">Live<span className="community-live-dot" aria-hidden="true" /></em>
      </div>
      <div className="pg-su" style={{ marginBottom: 28 }}>
        Every dot represents someone choosing results over busyness, right now.
      </div>

      <div className="community-broadcast-bar">
        <div className="community-broadcast-live">
          <span className="community-broadcast-dot" />
          Live
        </div>
        <div className="community-broadcast-count">{fmtCount(liveCount)}</div>
        <div className="community-broadcast-label">Total live on ResultOS</div>
      </div>

      {/* Stats row */}
      <div className="community-stats-row">
        <div className="community-stat">
            <div className="community-stat-val live">{fmtCount(liveCount)}</div>
          <div className="community-stat-lbl">Active Right Now</div>
        </div>
        <div className="community-stat-divider" />
        <div className="community-stat">
          <div className="community-stat-val sage">47</div>
          <div className="community-stat-lbl">Countries</div>
        </div>
        <div className="community-stat-divider" />
        <div className="community-stat">
          <div className="community-stat-val gold">Top 1%</div>
          <div className="community-stat-lbl">of Global Performers</div>
        </div>
      </div>

      {/* World map */}
      <div className="community-map-wrap">
        <svg viewBox="0 0 560 280" className="community-map-svg" aria-label="Live user map">

          <defs>
            <radialGradient id="communityOceanGlow" cx="50%" cy="42%" r="70%">
              <stop offset="0%" stopColor="rgba(46,201,160,0.12)" />
              <stop offset="58%" stopColor="rgba(77,143,224,0.05)" />
              <stop offset="100%" stopColor="rgba(3,17,25,0)" />
            </radialGradient>
            <linearGradient id="communityLandGlow" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(110,228,196,0.2)" />
              <stop offset="100%" stopColor="rgba(77,143,224,0.09)" />
            </linearGradient>
          </defs>

          <rect width="560" height="280" fill="#050c18" rx="8" />
          <ellipse cx="280" cy="140" rx="262" ry="124" fill="url(#communityOceanGlow)" />
          <ellipse cx="280" cy="140" rx="258" ry="121" fill="none" stroke="rgba(110,228,196,0.13)" strokeWidth="0.8" />

          {/* Lat/lon grid */}
          {[-60,-30,0,30,60].map(lat => {
            const y = (90 - lat) / 180 * 280
            return <path key={`lat${lat}`} d={`M22 ${y}C122 ${y - 12} 438 ${y - 12} 538 ${y}`} fill="none" stroke="rgba(255,255,255,0.045)" strokeWidth="0.6" />
          })}
          {[-150,-120,-90,-60,-30,0,30,60,90,120,150].map(lon => {
            const x = (lon + 180) / 360 * 560
            return <path key={`lon${lon}`} d={`M${x} 25C${x - 18} 84 ${x - 18} 196 ${x} 255`} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.6" />
          })}

          <g className="community-land">
            {LAND_PATHS.map((path, index) => (
              <path key={index} d={path} />
            ))}
          </g>

          {/* User dots */}
          {DOTS.map((dot, i) => (
            <g key={i}>
              <circle
                cx={dot.x} cy={dot.y}
                r={dot.r * 2.8}
                fill="none"
                stroke="rgba(110,228,196,0.55)"
                strokeWidth="0.8"
                className="community-dot-pulse"
                style={{ animationDelay: `${dot.d}s` }}
              />
              <circle
                cx={dot.x} cy={dot.y}
                r={dot.r}
                fill="var(--sage2)"
                style={{ filter: 'drop-shadow(0 0 5px rgba(110,228,196,0.9))' }}
              />
            </g>
          ))}
        </svg>

        <div className="community-map-legend">
          <span className="community-legend-dot" />
          Active ResultOS user
        </div>
      </div>

      <div className="community-live-members" aria-label="Recent anonymized live members">
        <div className="community-live-members-head">
          <div className="c-lbl">Live Now</div>
          <div className="community-privacy-note">First-name signals only</div>
        </div>
        <div className="community-member-grid">
          {liveMembers.map((member, index) => (
            <div className="community-member" key={member.mono}>
              <div className="community-member-avatar">{member.mono}</div>
              <div className="community-member-copy">
                <div className="community-member-name">{member.name}</div>
                <div className="community-member-meta">{member.city} · {member.status}</div>
              </div>
              <div className="community-member-time">{index < 2 ? 'Now' : `${index + 1}m`}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Top 1% card */}
      <div className="card mb24" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="cg gold" />
        <div className="c-lbl" style={{ marginBottom: 20 }}>Why You Are Already in the Top 1%</div>

        <div className="community-planet-row">
          <div className="community-planet-item">
            <div className="community-planet-num">{WORLD_POPULATION}</div>
            <div className="community-planet-sub">People on Earth</div>
          </div>
          <div className="community-planet-arrow">→</div>
          <div className="community-planet-item">
            <div className="community-planet-num azure">{WORLD_ADULTS_LABEL}</div>
            <div className="community-planet-sub">Adults (18+)</div>
          </div>
          <div className="community-planet-arrow">→</div>
          <div className="community-planet-item">
            <div className="community-planet-num gold">{TOP_1_PCT_LABEL}</div>
            <div className="community-planet-sub">Top 1% — Elite Performers</div>
          </div>
        </div>

        <p style={{
          fontSize: '13px',
          color: 'var(--text2)',
          lineHeight: '1.7',
          margin: '20px 0 16px',
        }}>
          Out of roughly {WORLD_ADULTS_LABEL} adults alive today, fewer than 1 in 100 consistently sets outcome-based goals, measures focus, and reflects on performance. Most people never build that rhythm. <strong style={{ color: 'var(--text)' }}>You are building it here.</strong>
        </p>

        <div className="community-elite-banner">
          <span className="community-elite-icon">◈</span>
          <span>
            Every day you use ResultOS, the gap between you and the other 99% grows wider.
          </span>
        </div>
      </div>

      {/* Inspiration quote */}
      <div className="insight">
        <div className="iq">
          "The crowd measures busyness. The 1% measures results. They are not smarter — they are simply more deliberate, every single day."
        </div>
      </div>
    </div>
  )
}
